import { useState, useRef, useEffect, useCallback } from 'react';

interface VoiceRecorderResult {
  isRecording: boolean;
  isPaused: boolean; // ⏸️ Added pause state
  duration: number;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void; // ⏸️ Added pause function
  resumeRecording: () => void; // ⏸️ Added resume function
  audioBlob: Blob | null;
  reset: () => void;
  error: string | null;
}

export const useVoiceRecorder = (maxDurationMs: number = 10000): VoiceRecorderResult => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const accumulatedTimeRef = useRef<number>(0);

  const reset = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
    setIsPaused(false);
    setDuration(0);
    setAudioBlob(null);
    setError(null);
    chunksRef.current = [];
    accumulatedTimeRef.current = 0;
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      // Commit the precise final elapsed time BEFORE we stop the recorder.
      // The timer ticks at 100ms, so the duration state can be stale by up to
      // ~100ms when stop is pressed. Compute exactly from wall-clock here so
      // the saved audioDuration matches what the user actually recorded.
      if (mediaRecorderRef.current.state === 'recording') {
        const finalElapsed = accumulatedTimeRef.current + (Date.now() - startTimeRef.current);
        setDuration(Math.min(finalElapsed, maxDurationMs));
      } else if (mediaRecorderRef.current.state === 'paused') {
        // Already paused — accumulator is the truth.
        setDuration(Math.min(accumulatedTimeRef.current, maxDurationMs));
      }
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
    setIsPaused(false);
  }, [maxDurationMs]);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      // Save elapsed time to accumulator
      accumulatedTimeRef.current += (Date.now() - startTimeRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      startTimeRef.current = Date.now();
      
      // Restart timer
      timerRef.current = setInterval(() => {
        const currentElapsed = Date.now() - startTimeRef.current;
        const totalElapsed = accumulatedTimeRef.current + currentElapsed;
        
        setDuration(Math.min(totalElapsed, maxDurationMs));
        
        if (totalElapsed >= maxDurationMs) {
          stopRecording();
        }
      }, 100);
    }
  }, [maxDurationMs, stopRecording]);

  const startRecording = async () => {
    // Guard: if a recorder is still running from a previous attempt, stop and
    // tear it down before resetting state. Without this, calling start twice
    // back-to-back leaves the old stream open, which is what users perceive as
    // "weird seconds counting" — two parallel timers and streams interleaving.
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch { /* best-effort */ }
      try { mediaRecorderRef.current.stream.getTracks().forEach(t => t.stop()); } catch { /* best-effort */ }
    }
    reset();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      let mimeType = 'audio/mp4';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/aac';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/aac';
      }
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = ''; // Let browser decide
      }

      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      startTimeRef.current = Date.now();

      timerRef.current = setInterval(() => {
        const currentElapsed = Date.now() - startTimeRef.current;
        const totalElapsed = accumulatedTimeRef.current + currentElapsed;
        
        setDuration(Math.min(totalElapsed, maxDurationMs));
        
        if (totalElapsed >= maxDurationMs) {
          stopRecording();
        }
      }, 100);

    } catch (err: any) {
      setError(err.message || 'Could not access microphone');
      setIsRecording(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  return { isRecording, isPaused, duration, startRecording, stopRecording, pauseRecording, resumeRecording, audioBlob, reset, error };
};
